import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Grid, Table, TableHead, TableRow, TableCell, TableBody, Typography, CircularProgress, Tooltip,
} from '@mui/material';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import { getPrescription } from '../../api/pharmacy';
import { formatDateTime } from '../../utils';
import {
  extractPatientClinicalAlerts,
  mapPrescription,
  mapPrescriptionLines,
} from '../../utils/pharmacyMappers';
import { isDispensablePrescriptionStatus } from '../../utils/prescriptionStatus';
import { previewPrescriptionPdfInNewTab, fetchPrescriptionPdfData } from '../../utils/prescriptionPdf';
import { IPD_COLORS, cardSx } from '../../theme';
import { lineTableSx } from './pharmacyStyles';
import { SummaryStat, DetailSummaryGrid, PatientClinicalAlertsBox } from './PharmacyUi';

export default function PrescriptionViewDialog({ open, prescription, onClose, onStartDispense }) {
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!open || !prescription?.id) {
      setDetail(null);
      setItems([]);
      return undefined;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    getPrescription(prescription.id)
      .then((data) => {
        if (!alive) return;
        setDetail(mapPrescription(data));
        setItems(mapPrescriptionLines(data));
      })
      .catch((e) => {
        if (alive) setError(e?.message || 'Failed to load prescription');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [open, prescription?.id]);

  const view = detail || prescription;
  const patientAlerts = useMemo(() => {
    if (!view?._raw) return [];
    return extractPatientClinicalAlerts(view._raw);
  }, [view]);
  const pendingItems = items.filter((i) => (i.prescribed_qty - (i.dispensed_qty || 0)) > 0);
  // Same gate as the list row: a dispensed, received or cancelled order has no
  // forward transition left, so offering Start Dispense here would only produce a
  // rejection (or a duplicate dispense) further down.
  // Optional chaining is load-bearing: this runs before the `!prescription` guard
  // below, and the dialog is mounted with a null prescription until a row is picked.
  const canDispense = isDispensablePrescriptionStatus(view?.status);

  async function handleViewPdf() {
    setPdfLoading(true);
    try {
      const data = await fetchPrescriptionPdfData(prescription.id);
      previewPrescriptionPdfInNewTab(data);
    } catch (e) {
      console.error('Prescription PDF preview failed', e);
    } finally {
      setPdfLoading(false);
    }
  }

  if (!prescription) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        color: IPD_COLORS.textPrimary, pb: 1,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px',
          backgroundColor: IPD_COLORS.selectedBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <LocalPharmacyIcon sx={{ color: IPD_COLORS.primary }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{view.order_number || view.uhid || view.id}</Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {view.patient_name}
            {view.created_at ? ` · Ordered ${formatDateTime(view.created_at)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: IPD_COLORS.border }}>
        <DetailSummaryGrid>
          <Grid item xs={6} sm={3}><SummaryStat label="Patient" value={view.patient_name} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="UHID" value={view.uhid || view.order_number} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Doctor" value={view.doctor_name} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Location" value={view.ward} /></Grid>
          <Grid item xs={6} sm={3}><SummaryStat label="Ordered" value={formatDateTime(view.created_at)} /></Grid>
        </DetailSummaryGrid>

        {(view.provisional_diagnosis || view.final_diagnosis) && (
          <Box sx={{ mb: 2 }}>
            {view.provisional_diagnosis && (
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 0.5 }}>
                <strong>Provisional:</strong> {view.provisional_diagnosis}
              </Typography>
            )}
            {view.final_diagnosis && (
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                <strong>Final:</strong> {view.final_diagnosis}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <StatusChip status={view.status} domain="prescription" />
          {view.has_controlled && <StatusChip status="AWAITING_AUTHORIZATION" />}
          {view.has_alerts && <StatusChip status="FAILED" />}
        </Box>

        <PatientClinicalAlertsBox alerts={patientAlerts} patientName={view.patient_name} />

        <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary, mb: 1.5 }}>
          Prescribed Medicines ({loading ? '…' : items.length})
        </Typography>

        {loading && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        )}

        {error && (
          <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 13, mb: 2 }}>{error}</Typography>
        )}

        {!loading && !error && (
          <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Medicine</TableCell>
                  <TableCell>Dose</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>{item.medicine}</TableCell>
                    <TableCell>{item.dosage || '—'}</TableCell>
                    <TableCell>{item.frequency || '—'}</TableCell>
                    <TableCell>{item.duration || '—'}</TableCell>
                    {/* Same rule as the dispense screen: `prescribed_qty` falls back
                        to a duration-days estimate, and showing that unlabelled read
                        as an order for exactly as many units as the course had days. */}
                    <TableCell align="right">
                      {item.qty_specified ? item.prescribed_qty : (
                        <Tooltip title="Quantity not specified by prescriber">
                          <span>—</span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Close
        </Button>
        <InvButton variant="outlined" tone="teal" size="small"
          startIcon={<PictureAsPdfIcon />} disabled={pdfLoading}
          onClick={handleViewPdf}>
          {pdfLoading ? 'Generating…' : 'View PDF'}
        </InvButton>
        {canDispense && pendingItems.length > 0 && onStartDispense && (
          <InvButton onClick={() => { onClose(); onStartDispense?.(prescription); }}>
            Start Dispense ({pendingItems.length} medicine{pendingItems.length > 1 ? 's' : ''})
          </InvButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
